import React, { useState, useEffect, useCallback } from "react";
import { usePassword } from "../context/PasswordContext";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { UserAuth } from "../context/AuthContext";
import { useContent } from "../context/ContentContext";
import { Lock, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";

const AuthorizedSeries = () => {
  const { authorizedSeries, setAuthorizedSeries } = usePassword();
  const { user } = UserAuth();
  const { series: allSeries } = useContent();
  const [loading, setLoading] = useState(true);
  const [seriesList, setSeriesList] = useState([]);

  // Memoize the update function to prevent unnecessary re-renders
  const updateAuthorizedSeries = useCallback(async () => {
    if (!authorizedSeries?.length || !allSeries?.length) {
      setSeriesList([]);
      return;
    }

    // Filter invalid series and get details
    const detailedList = authorizedSeries
      .filter((auth) => {
        const seriesExists = allSeries.some((s) => s.id === auth.seriesId);
        if (!seriesExists) return false;

        const seriesData = allSeries.find((s) => s.id === auth.seriesId);
        return seriesData?.password === auth.password;
      })
      .map((auth) => {
        const seriesDetails = allSeries.find((s) => s.id === auth.seriesId);
        return {
          ...auth,
          title: seriesDetails?.title || "Unknown Series",
          thumbnail: seriesDetails?.thumbnail,
        };
      });

    setSeriesList(detailedList);

    // Only update Firestore if the list has actually changed
    if (JSON.stringify(detailedList) !== JSON.stringify(authorizedSeries)) {
      if (user?.uid) {
        const userDocRef = doc(db, "users", user.uid);
        try {
          await updateDoc(userDocRef, {
            authorizedSeries: detailedList,
          });
          setAuthorizedSeries(detailedList);
        } catch (error) {
          console.error("Error updating Firestore:", error);
        }
      }
    }
  }, [allSeries, authorizedSeries, user?.uid, setAuthorizedSeries]);

  // Remove the separate loading effect and handle loading in the main update
  useEffect(() => {
    setLoading(!(allSeries?.length > 0));
  }, [allSeries]);

  // Modify the useEffect for series updates
  useEffect(() => {
    if (allSeries?.length > 0) {
      updateAuthorizedSeries();
    }
  }, [updateAuthorizedSeries, allSeries]);

  const revokeAccess = async (seriesId, password) => {
    const confirmRevoke = window.confirm(
      "Are you sure you want to remove access to this series?"
    );
    if (!confirmRevoke) return;

    try {
      // Create new array without the revoked series
      const updatedSeries = authorizedSeries.filter(
        (item) => !(item.seriesId === seriesId && item.password === password)
      );

      // Update both localStorage and Firestore regardless of user state
      localStorage.removeItem("authorizedSeries");

      if (user?.uid) {
        const userDocRef = doc(db, "users", user.uid);
        await updateDoc(userDocRef, {
          authorizedSeries: updatedSeries,
        });
      }

      // Update context state
      setAuthorizedSeries(updatedSeries);

      // Update local component state
      setSeriesList((prevList) =>
        prevList.filter(
          (item) => !(item.seriesId === seriesId && item.password === password)
        )
      );
    } catch (error) {
      console.error("Error revoking access:", error);
      alert("Failed to remove access. Please try again.");
    }
  };

  if (loading) {
    return <div className="p-4 text-white">Loading authorized series...</div>;
  }

  if (seriesList.length === 0) {
    return (
      <div className="p-4 text-white">
        You don't have access to any password-protected series.
      </div>
    );
  }

  return (
    <div className="p-4">
      <h2 className="text-2xl font-bold mb-4">
        Password-Protected Series Access
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {seriesList.map((series) => (
          <div
            key={`${series.seriesId}-${series.password}`}
            className="bg-gray-800 rounded-lg overflow-hidden shadow-lg"
          >
            <div className="p-4 flex items-start justify-between">
              <Link to={`/series/${series.seriesId}`} className="block">
                <div className="flex items-center">
                  {series.thumbnail && (
                    <img
                      src={series.thumbnail}
                      alt={series.title}
                      className="h-16 object-cover aspect-video rounded mr-3"
                    />
                  )}
                  <div>
                    <h3 className="font-bold">{series.title}</h3>
                    <div className="flex items-center text-yellow-400 text-sm">
                      <Lock size={14} className="mr-1" />
                      <span>Password Protected</span>
                    </div>
                  </div>
                </div>
              </Link>
              <button
                onClick={() => revokeAccess(series.seriesId, series.password)}
                className="p-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                title="Remove Access"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AuthorizedSeries;
